import React, { useState, useEffect, useMemo } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { transactionApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Transaction {
  id: string;
  studentId: string;
  studentName: string;
  type: string | number;
  amount: number;
  balanceAfter: number;
  createdAt: string;
  description: string | null;
  paymentGatewayOrderId: string | null;
  paymentGatewayTransactionId: string | null;
  walletCodeId: string | null;
  walletCodeCode: string | null;
  itemId: string | null;
  itemType: string | null;
  itemPrice: number | null;
}

interface FilterData {
  studentId: string;
  type: string;
  fromDate: string;
  toDate: string;
}

interface SortConfig {
  key: keyof Transaction;
  direction: 'asc' | 'desc';
}

const EXPORT_PAGE_SIZE = 100;

function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function sortTransactionList(transactions: Transaction[], sortConfig: SortConfig | null): Transaction[] {
  const sortableTransactions = [...transactions];
  if (sortConfig !== null) {
    sortableTransactions.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableTransactions;
}

const Transactions: React.FC = () => {
  const { isModerator } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState<FilterData>({
    studentId: '',
    type: '',
    fromDate: '',
    toDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterData>({
    studentId: '',
    type: '',
    fromDate: '',
    toDate: '',
  });
  const [exporting, setExporting] = useState(false);

  const getTransactionTypeLabel = (type: string | number): string => {
    const typeNum = typeof type === 'string' ? parseInt(type, 10) : type;
    if (isNaN(typeNum)) return String(type);
    
    switch (typeNum) {
      case 0:
        return 'شراء مباشر';
      case 1:
        return 'شراء محفظة';
      case 2:
        return 'شحن بكود محفظة';
      case 3:
        return 'استخدام محفظة';
      case 4:
        return 'الأدمن شحن المحفظة';
      case 5:
        return 'تسجيل من الأدمن';
      default:
        return String(type);
    }
  };

  const getTransactionTypeColor = (type: string | number): string => {
    const typeNum = typeof type === 'string' ? parseInt(type, 10) : type;
    if (isNaN(typeNum)) return 'bg-gray-100 text-gray-800';
    
    switch (typeNum) {
      case 0: // DirectCoursePurchase
      case 1: // BuyCoinsDirect
        return 'bg-blue-100 text-blue-800';
      case 2: // WalletChargeByCode
      case 4: // AdminCoinsGrant
        return 'bg-green-100 text-green-800';
      case 3: // WalletCoinsUsage
        return 'bg-yellow-100 text-yellow-800';
      case 5: // AdminEnrollment
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchTransactions = async () => {
    if (!isModerator()) {
      setError('ليس لديك صلاحية لعرض التحويلات.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const typeValue = appliedFilters.type ? parseInt(appliedFilters.type, 10) : null;
      const response = await transactionApi.getTransactions(
        currentPage,
        itemsPerPage,
        appliedFilters.studentId || null,
        typeValue,
        appliedFilters.fromDate || null,
        appliedFilters.toDate || null
      );

      if (response.success) {
        const transactionData = response.data;
        setTransactions(transactionData.data || []);
        setTotalCount(transactionData.totalCount || 0);
        setTotalPages(transactionData.totalPages || 0);
      } else {
        throw new Error(response.message || 'فشل في جلب التحويلات');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('فشل في تحميل التحويلات. يرجى المحاولة مرة أخرى لاحقاً.');
      setTransactions([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModerator()) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, isModerator]);

  const handleSort = (key: keyof Transaction) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTransactions = useMemo(
    () => sortTransactionList(transactions, sortConfig),
    [transactions, sortConfig]
  );

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      studentId: '',
      type: '',
      fromDate: '',
      toDate: '',
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return `${amount.toFixed(2)} ريال`;
  };

  const handleDownloadCsv = async () => {
    if (!isModerator() || totalCount === 0) return;

    setExporting(true);
    setError(null);
    try {
      const typeValue = appliedFilters.type ? parseInt(appliedFilters.type, 10) : null;
      const first = await transactionApi.getTransactions(
        1,
        EXPORT_PAGE_SIZE,
        appliedFilters.studentId || null,
        typeValue,
        appliedFilters.fromDate || null,
        appliedFilters.toDate || null
      );

      if (!first.success) {
        throw new Error(first.message || 'فشل في جلب التحويلات للتصدير');
      }

      const transactionData = first.data;
      const allRows: Transaction[] = transactionData.data || [];
      const pages = transactionData.totalPages || 1;

      for (let page = 2; page <= pages; page++) {
        const response = await transactionApi.getTransactions(
          page,
          EXPORT_PAGE_SIZE,
          appliedFilters.studentId || null,
          typeValue,
          appliedFilters.fromDate || null,
          appliedFilters.toDate || null
        );
        if (!response.success) {
          throw new Error(response.message || 'فشل في جلب التحويلات للتصدير');
        }
        const rows = response.data.data || [];
        allRows.push(...rows);
      }

      const sorted = sortTransactionList(allRows, sortConfig);
      const headers = [
        'التاريخ',
        'رقم الطالب',
        'اسم الطالب',
        'النوع',
        'المبلغ',
        'الرصيد بعد العملية',
        'الوصف',
        'رقم طلب بوابة الدفع',
        'رقم معاملة بوابة الدفع',
        'كود المحفظة',
        'رقم العنصر',
        'نوع العنصر',
        'سعر العنصر',
      ];
      const csvRows = sorted.map((transaction) => [
        escapeCsvCell(formatDate(transaction.createdAt)),
        escapeCsvCell(transaction.studentId),
        escapeCsvCell(transaction.studentName || '-'),
        escapeCsvCell(getTransactionTypeLabel(transaction.type)),
        escapeCsvCell(formatCurrency(transaction.amount)),
        escapeCsvCell(formatCurrency(transaction.balanceAfter)),
        escapeCsvCell(transaction.description || '-'),
        escapeCsvCell(transaction.paymentGatewayOrderId || '-'),
        escapeCsvCell(transaction.paymentGatewayTransactionId || '-'),
        escapeCsvCell(transaction.walletCodeCode || '-'),
        escapeCsvCell(transaction.itemId || '-'),
        escapeCsvCell(transaction.itemType || '-'),
        escapeCsvCell(transaction.itemPrice !== null ? formatCurrency(transaction.itemPrice) : '-'),
      ]);

      const BOM = '\uFEFF';
      const csvContent =
        BOM + [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting transactions:', e);
      setError('فشل في تصدير التحويلات. يرجى المحاولة مرة أخرى لاحقاً.');
    } finally {
      setExporting(false);
    }
  };

  if (!isModerator()) {
    return (
      <div className="p-4 md:p-6" dir="rtl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">ليس لديك صلاحية لعرض التحويلات.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" dir="rtl">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">التحويلات</h1>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الطالب</label>
            <input
              type="text"
              value={filters.studentId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, studentId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="البحث برقم الطالب"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">نوع التحويل</label>
            <select
              value={filters.type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الأنواع</option>
              <option value="0">شراء مباشر</option>
              <option value="1">شراء محفظة</option>
              <option value="2">شحن بكود محفظة</option>
              <option value="3">استخدام محفظة</option>
              <option value="4">الأدمن شحن المحفظة</option>
              <option value="5">تسجيل من الأدمن</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, fromDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, toDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={handleClearFilters}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            مسح
          </button>
          <button
            onClick={handleSearch}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            بحث
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="font-medium">الإجمالي: {totalCount}</span>
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={totalCount === 0 || exporting}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
            ) : (
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
            )}
            {exporting ? 'جاري التصدير...' : 'تحميل CSV'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'createdAt', label: 'التاريخ' },
                  { key: 'studentId', label: 'رقم الطالب' },
                  { key: 'studentName', label: 'اسم الطالب' },
                  { key: 'type', label: 'النوع' },
                  { key: 'amount', label: 'المبلغ' },
                  { key: 'balanceAfter', label: 'الرصيد بعد العملية' },
                  { key: 'description', label: 'الوصف' },
                  { key: 'walletCodeCode', label: 'كود المحفظة' },
                  { key: 'paymentGatewayOrderId', label: 'رقم طلب الدفع' },
                  { key: 'itemType', label: 'نوع العنصر' },
                  { key: 'itemPrice', label: 'سعر العنصر' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key as keyof Transaction)}
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    {label}
                    {sortConfig?.key === key && (
                      <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-4 text-center text-gray-500">
                    لا توجد تحويلات مطابقة للبحث
                  </td>
                </tr>
              ) : (
                sortedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(transaction.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-xs">{transaction.studentId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{transaction.studentName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTransactionTypeColor(transaction.type)}`}>
                        {getTransactionTypeLabel(transaction.type)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                      transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{formatCurrency(transaction.balanceAfter)}</td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate" title={transaction.description || '-'}>
                      {transaction.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{transaction.walletCodeCode || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-xs">
                      {transaction.paymentGatewayOrderId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{transaction.itemType || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {transaction.itemPrice !== null ? formatCurrency(transaction.itemPrice) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>{' '}
                من <span className="font-medium">{totalCount}</span> نتيجة
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&lsaquo;</span>
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;
